import { useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  DashboardIcon,
  PeopleIcon,
  ReceiptIcon,
  InventoryIcon,
  SettingsIcon,
  IconButton,
  MenuIcon,
  SearchIcon,
  WalletIcon,
  CalendarIcon,
  CalculatorIcon,
  StoreIcon,
} from '@/ui'
import { GlobalSearch } from './GlobalSearch.js'
import { ThemeToggle } from './ThemeToggle.js'
import { LanguageToggle } from './LanguageToggle.js'
import { useAuth } from './AuthGate.js'
import { useSettings } from '@/lib/hooks.js'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api.js'

/**
 * Persistent frame: stark monochrome console sidebar (desktop) or off-canvas drawer (mobile),
 * main content wrapped in page-column. No top app bar on desktop; search moves to sidebar.
 * - Desktop (md and up): fixed left sidebar 260px wide, full height
 * - Mobile (below md): off-canvas drawer, hamburger toggle in slim top bar
 */

interface NavItem {
  label: string
  to: string
  icon: React.ReactNode
}

interface NavSection {
  label?: string
  items: NavItem[]
}

type NavSectionValue = import('@garage/shared').StaffSection | import('@garage/shared').StaffSection[]

const NAV_SECTION: Partial<Record<string, NavSectionValue>> = {
  '/jobcards': 'jobcards',
  '/accounting': ['finance', 'accounting'],
  '/storage': 'storage',
  '/suppliers': 'suppliers',
  '/clients': 'clients',
  '/staff': 'workers',
  '/calendar': 'calendar',
  '/catalog': 'catalog',
  '/settings': 'settings',
}

export function AppShell() {
  const location = useLocation()
  const { t } = useTranslation()
  const { account } = useAuth()
  const { data: settings } = useSettings()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)

  const allNavItems: NavSection[] = [
    {
      items: [{ label: t('nav.dashboard'), to: '/', icon: <DashboardIcon /> }],
    },
    {
      label: t('nav.workshop'),
      items: [
        { label: t('nav.jobcards'), to: '/jobcards', icon: <ReceiptIcon /> },
        { label: t('nav.accounting'), to: '/accounting', icon: <CalculatorIcon /> },
        { label: t('nav.storage'), to: '/storage', icon: <InventoryIcon /> },
        { label: t('nav.suppliers'), to: '/suppliers', icon: <StoreIcon /> },
        { label: t('nav.clients'), to: '/clients', icon: <PeopleIcon /> },
        { label: t('nav.staff'), to: '/staff', icon: <WalletIcon /> },
        { label: t('nav.calendar'), to: '/calendar', icon: <CalendarIcon /> },
      ],
    },
    {
      label: t('nav.setup'),
      items: [
        { label: t('nav.catalog'), to: '/catalog', icon: <InventoryIcon /> },
        { label: t('nav.settings'), to: '/settings', icon: <SettingsIcon /> },
      ],
    },
  ]

  // Filter nav items based on account sections (admin always sees all)
  const navSections: NavSection[] = account.role === 'admin'
    ? allNavItems
    : allNavItems
      .map(section => ({
        ...section,
        items: section.items.filter(item => {
          // Dashboard is always shown
          if (item.to === '/') return true
          // Check if account has access to this section
          const requiredSections = NAV_SECTION[item.to]
          if (!requiredSections) return true
          // Normalize to array and check if account has any of the required sections
          const sectionArray = Array.isArray(requiredSections) ? requiredSections : [requiredSections]
          return sectionArray.some(s => account.sections.includes(s))
        }),
      }))
      .filter(section => section.items.length > 0) // Hide empty sections

  const businessName = settings?.businessName || 'Cypek Garage'

  // Determine if a nav item is active: exact match for '/', prefix match for others.
  const isActive = (to: string): boolean => {
    if (to === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(to)
  }

  // Close sidebar on navigation.
  const handleNavClick = () => {
    setSidebarOpen(false)
  }

  // Sidebar content shared by desktop and mobile.
  const sidebarContent = (
    <>
      {/* Sidebar header with business name and search button. */}
      <div className="px-3 py-4 flex items-center justify-between gap-2 border-b border-outline-variant">
        <h1 className="text-title-md text-on-surface">{businessName}</h1>
        <IconButton
          icon={<SearchIcon />}
          label={t('nav.search')}
          variant="standard"
          size="sm"
          onClick={() => setSearchOpen(true)}
        />
      </div>

      {/* Navigation sections. */}
      <nav className="flex-1 overflow-y-auto overscroll-y-contain px-0">
        {navSections.map((section, sectionIndex) => (
          <div key={sectionIndex}>
            {section.label && (
              <div className="text-label-sm text-on-surface-subtle uppercase tracking-wide px-3 mb-1 mt-6">
                {section.label}
              </div>
            )}
            {section.items.map(item => (
              <a
                key={item.to}
                href={item.to}
                onClick={handleNavClick}
                className={`flex items-center gap-3 h-9 px-3 rounded-md text-body-md interactive ${
                  isActive(item.to)
                    ? 'bg-surface-highest text-on-surface'
                    : 'text-on-surface-variant hover:text-on-surface hover:bg-surface-high'
                }`}
                aria-current={isActive(item.to) ? 'page' : undefined}
              >
                <div className="h-4 w-4 flex items-center justify-center flex-shrink-0">{item.icon}</div>
                <span className="truncate">{item.label}</span>
              </a>
            ))}
          </div>
        ))}
      </nav>

      {/* Sidebar footer: who's signed in, theme/language toggles, and version. */}
      <div className="border-t border-outline-variant p-3 flex flex-col gap-3">
        <SignedInAs />
        <div className="flex items-center gap-1">
          <ThemeToggle />
          <LanguageToggle />
        </div>
        <BuildStamp />
      </div>
    </>
  )

  return (
    <div className="flex app-h bg-background text-on-background">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-sidebar bg-background border-e border-outline-variant">
        {sidebarContent}
      </aside>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile-only top bar with hamburger and search. */}
        <header className="md:hidden sticky top-0 z-40 bg-background border-b border-outline-variant px-3 py-3 flex items-center justify-between h-14">
          <IconButton
            icon={<MenuIcon />}
            label={t('nav.menu')}
            variant="standard"
            size="sm"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          />
          <h1 className="text-title-md text-on-surface flex-1 text-center px-2">{businessName}</h1>
          <IconButton
            icon={<SearchIcon />}
            label={t('nav.search')}
            variant="standard"
            size="sm"
            onClick={() => setSearchOpen(true)}
          />
        </header>

        {/* Mobile sidebar drawer with scrim. */}
        {sidebarOpen && (
          <>
            {/* Scrim. */}
            <div
              className="fixed inset-0 z-30 bg-black/50 md:hidden"
              onClick={() => setSidebarOpen(false)}
              aria-hidden
            />
            {/* Drawer. */}
            <aside className="fixed inset-y-0 start-0 z-40 w-sidebar bg-background border-e border-outline-variant flex flex-col md:hidden">
              {sidebarContent}
            </aside>
          </>
        )}

        {/* Main content.
            overscroll-y-contain stops scroll CHAINING: without it, once this
            element's own scroll hits bottom, leftover wheel/touch input
            passes to the outer page. html/body report no real overflow of
            their own, but the browser still moves them — the whole app
            (built for exactly one viewport, h-screen) slides out from under
            itself, leaving nothing but body's plain background below it. */}
        <main className="flex-1 min-w-0 overflow-y-auto overscroll-y-contain">
          {account.readOnly && (
            <div className="bg-surface-variant border-b border-outline-variant px-4 py-2">
              <p className="text-body-sm text-on-surface-variant text-center">
                {t('auth.readOnlyBanner')}
              </p>
            </div>
          )}
          <div className="page-column pt-8 pb-[calc(2rem+env(safe-area-inset-bottom))]">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Global search overlay: mounted once, visible at all breakpoints via portal. */}
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  )
}

/** Who is signed in, and a way out — every screen behind AuthGate has this. */
function SignedInAs() {
  const { t } = useTranslation()
  const { account, signOut, signingOut } = useAuth()

  return (
    <div className="flex items-center justify-between gap-2 text-label-sm text-on-surface-variant">
      <span className="truncate" title={account.displayName}>
        {account.displayName}
      </span>
      <button
        type="button"
        onClick={signOut}
        disabled={signingOut}
        className="text-on-surface-variant hover:text-on-surface interactive underline-offset-2 hover:underline disabled:opacity-40"
      >
        {signingOut ? t('auth.signingOut') : t('auth.signOut')}
      </button>
    </div>
  )
}

/**
 * The commit this build came from.
 *
 * A hardcoded "v1.0" cannot answer the only question anyone asks of a version
 * string here — did the update actually land? — so this reads what the server
 * reports instead. "unknown" means the image predates build stamping.
 */
function BuildStamp() {
  const { data } = useQuery({
    queryKey: ['version'],
    queryFn: () => api.version(),
    staleTime: Infinity,
    retry: false,
  })

  return (
    <div className="text-label-sm text-on-surface-subtle">
      Cypek Garage
      {data?.commit && data.commit !== 'unknown' && <span className="ref"> {data.commit}</span>}
    </div>
  )
}
